import { apenasDigitos } from './validacao.js';

/**
 * Máscaras de campos brasileiros, usadas tanto ao digitar quanto ao exibir.
 *
 * São **progressivas**: funcionam com o valor incompleto, aplicando só a pontuação que
 * já cabe. "1234" vira "123.4", não "123.4__-__". Isso é o que permite usar a mesma
 * função no `onChange` do campo sem o cursor pular nem aparecer pontuação órfã.
 *
 * O valor guardado no banco é sempre **só os dígitos** — máscara é apresentação. Quem
 * grava usa `apenasDigitos`, quem mostra usa daqui.
 */

/**
 * Aplica um padrão sobre os dígitos disponíveis. `#` é posição de dígito; qualquer outro
 * caractere é pontuação, inserida apenas quando ainda houver dígito depois dela.
 */
function aplicar(valor: string, padrao: string, maximo: number): string {
  const digitos = apenasDigitos(valor).slice(0, maximo);
  if (digitos.length === 0) return '';

  let saida = '';
  let i = 0;

  for (const caractere of padrao) {
    if (i >= digitos.length) break;
    if (caractere === '#') {
      saida += digitos[i];
      i++;
    } else {
      saida += caractere;
    }
  }

  return saida;
}

export const mascararCPF = (valor: string): string => aplicar(valor, '###.###.###-##', 11);
export const mascararCNPJ = (valor: string): string => aplicar(valor, '##.###.###/####-##', 14);
export const mascararCEP = (valor: string): string => aplicar(valor, '#####-###', 8);

/**
 * Telefone com DDD, fixo ou celular. O formato só se decide no décimo primeiro dígito,
 * então até lá vale o de oito — senão o nono dígito do celular empurraria a máscara
 * errada enquanto a pessoa ainda está digitando.
 */
export function mascararTelefone(valor: string): string {
  const digitos = apenasDigitos(valor).slice(0, 11);
  return digitos.length > 10
    ? aplicar(digitos, '(##) #####-####', 11)
    : aplicar(digitos, '(##) ####-####', 10);
}

/** CPF ou CNPJ, pelo tamanho. Abaixo de 12 dígitos ainda pode virar qualquer um dos dois. */
export function mascararDocumento(valor: string): string {
  return apenasDigitos(valor).length > 11 ? mascararCNPJ(valor) : mascararCPF(valor);
}

/**
 * Valor em reais, digitado da direita para a esquerda: "6000" vira "60,00", "600000" vira
 * "6.000,00".
 *
 * É a mesma disciplina das outras máscaras — o que trafega são só dígitos, e aqui eles
 * **são os centavos**. Não existe conversão de reais para centavos no caminho, porque é
 * nela que mora o erro de fator 100 numa venda de seis dígitos: o campo já entrega ao
 * domínio o inteiro que o sistema grava.
 *
 * Onze dígitos dão conta de R$ 999.999.999,99 — folga sobrando para a maior cadeira.
 */
export function mascararDinheiro(valor: string): string {
  const digitos = apenasDigitos(valor)
    .slice(0, 11)
    .replace(/^0+(?=\d)/, '');
  if (digitos === '') return '';

  const centavos = digitos.padStart(3, '0');
  const reais = centavos.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${reais},${centavos.slice(-2)}`;
}

/** Centavos inteiros a partir do que a máscara de dinheiro entrega. `null` se vazio. */
export function dinheiroParaCentavos(valor: string): number | null {
  const digitos = apenasDigitos(valor).slice(0, 11);
  return digitos === '' ? null : Number(digitos);
}

/** Sigla de estado: duas letras maiúsculas, sem acento nem número. */
export function mascararUF(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/[^a-zA-Z]/g, '')
    .slice(0, 2)
    .toUpperCase();
}

export type TipoDeMascara = 'cpf' | 'cnpj' | 'documento' | 'cep' | 'telefone' | 'uf' | 'dinheiro';

const MASCARAS: Record<TipoDeMascara, (valor: string) => string> = {
  cpf: mascararCPF,
  cnpj: mascararCNPJ,
  documento: mascararDocumento,
  cep: mascararCEP,
  telefone: mascararTelefone,
  uf: mascararUF,
  dinheiro: mascararDinheiro,
};

export function mascarar(tipo: TipoDeMascara, valor: string): string {
  return MASCARAS[tipo](valor);
}

/**
 * Quantos caracteres o campo pode ter já mascarado — serve de `maxLength` no input, para
 * o navegador barrar antes de a máscara precisar truncar.
 */
export const TAMANHO_MASCARADO: Record<TipoDeMascara, number> = {
  cpf: 14,
  cnpj: 18,
  documento: 18,
  cep: 9,
  telefone: 15,
  uf: 2,
  // 11 dígitos + "," + 3 pontos de milhar.
  dinheiro: 15,
};
